/**
 * Frontend security hardening — runs once at app boot.
 *
 * 1. Injects a Content-Security-Policy <meta> tag
 * 2. Injects security-related <meta> headers
 * 3. In production: blocks devtools shortcuts and source inspection
 * 4. General protections: noopener for links, drag prevention
 */

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

const API_ORIGIN = (() => {
  try {
    return new URL(process.env.VUE_APP_API_BASE_URL).origin;
  } catch {
    return '';
  }
})();

function injectCSP() {
  if (document.querySelector('meta[http-equiv="Content-Security-Policy"]')) return;

  const directives = [
    "default-src 'self'",
    IS_PRODUCTION
      ? "script-src 'self' https://www.google.com https://www.gstatic.com"
      : "script-src 'self' 'unsafe-eval' https://www.google.com https://www.gstatic.com",
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://cdnjs.cloudflare.com",
    "font-src 'self' https://fonts.gstatic.com https://cdnjs.cloudflare.com data:",
    `img-src 'self' data: blob: https://flagcdn.com https://maps.googleapis.com ${API_ORIGIN}`.trim(),
    `connect-src 'self' ${API_ORIGIN} https://maps.googleapis.com https://www.google.com`.trim(),
    "frame-src 'self' https://maps.google.com https://www.google.com https://www.gstatic.com",
    "object-src 'none'",
    "base-uri 'self'",
    "form-action 'self'",
  ];

  const meta = document.createElement('meta');
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = directives.join('; ');
  document.head.prepend(meta);
}

function injectSecurityHeaders() {
  const noSniff = document.createElement('meta');
  noSniff.httpEquiv = 'X-Content-Type-Options';
  noSniff.content = 'nosniff';
  document.head.appendChild(noSniff);

  // X-Frame-Options must be set as HTTP header, not meta tag — skip here

  let referrer = document.querySelector('meta[name="referrer"]');
  if (!referrer) {
    referrer = document.createElement('meta');
    referrer.name = 'referrer';
    referrer.content = 'strict-origin-when-cross-origin';
    document.head.appendChild(referrer);
  }
}

/**
 * Production-only: block devtools shortcuts (right-click allowed).
 */
function blockInspection() {
  if (!IS_PRODUCTION) return;

  document.addEventListener('keydown', (e) => {
    if (e.key === 'F12' || e.keyCode === 123) { e.preventDefault(); return false; }
    if (e.ctrlKey && e.shiftKey && ['I','i','J','j','C','c'].includes(e.key)) { e.preventDefault(); return false; }
    if (e.ctrlKey && (e.key === 'u' || e.key === 'U')) { e.preventDefault(); return false; }
    if (e.ctrlKey && (e.key === 's' || e.key === 'S') && !e.target.closest('input,textarea')) { e.preventDefault(); return false; }
  });

  document.addEventListener('dragstart', (e) => {
    if (e.target.tagName === 'IMG' || e.target.tagName === 'A') e.preventDefault();
  });
}

/**
 * General protections (all environments)
 */
function applyGeneralProtections() {
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[target="_blank"]');
    if (a && !a.rel?.includes('noopener')) {
      a.rel = (a.rel || '') + ' noopener noreferrer';
    }
  });
}

function harden() {
  injectCSP();
  injectSecurityHeaders();
  blockInspection();
  applyGeneralProtections();
}

export { harden };
